import { getActiveQuestions } from '@/utils/formQuestionShowCondition';

/** Máximo de alternativas em perguntas de múltipla escolha do match. */
export const MATCH_MAX_MULTIPLE_SELECTIONS = 3;

export interface MatchInterstitialContent {
	/** Fração do questionário após a qual o interstício aparece (0..1). */
	afterFraction: number;
	eyebrow: string;
	title: string;
	body: string;
}

/** Feedbacks curtos no meio do caminho (estilo onboarding Preply). */
export const MATCH_INTERSTITIALS: MatchInterstitialContent[] = [
	{
		afterFraction: 1 / 3,
		eyebrow: 'Obrigado por compartilhar',
		title: 'Isso já nos ajuda a entender o que você precisa.',
		body: 'Agora, um pouco sobre sua experiência com terapia. Não existe resposta errada.',
	},
	{
		afterFraction: 2 / 3,
		eyebrow: 'Quase lá',
		title: 'Não existe abordagem certa ou errada.',
		body: 'O que importa é a conexão com quem te acompanha. Só mais algumas preferências práticas.',
	},
];

export type MatchFlowStep =
	| { kind: 'question'; question: Forms.FormQuestion; position: number }
	| {
			kind: 'interstitial';
			content: MatchInterstitialContent;
			index: number;
	  };

/**
 * Monta a sequência de telas a partir das perguntas ativas (respeitando show_condition)
 * e insere os interstícios nas posições calculadas.
 */
export function buildMatchFlow(
	questions: Forms.FormQuestion[],
	answers: Record<string, string | null>,
): { steps: MatchFlowStep[]; activeQuestions: Forms.FormQuestion[] } {
	const activeQuestions = getActiveQuestions(questions, answers);
	const total = activeQuestions.length;
	const steps: MatchFlowStep[] = [];

	const interstitialAfter = new Map<number, MatchInterstitialContent[]>();
	if (total >= 4) {
		MATCH_INTERSTITIALS.forEach((content) => {
			const afterIndex = Math.round(total * content.afterFraction) - 1;
			if (afterIndex < 0 || afterIndex >= total - 1) return;
			const list = interstitialAfter.get(afterIndex) ?? [];
			list.push(content);
			interstitialAfter.set(afterIndex, list);
		});
	}

	let interstitialIndex = 0;
	activeQuestions.forEach((question, index) => {
		steps.push({ kind: 'question', question, position: index + 1 });
		const after = interstitialAfter.get(index);
		if (after) {
			after.forEach((content) => {
				steps.push({
					kind: 'interstitial',
					content,
					index: interstitialIndex++,
				});
			});
		}
	});

	return { steps, activeQuestions };
}

/** Chaves selecionadas de uma resposta de múltipla escolha ("AC" -> ["A","C"]). */
export function splitMultipleAnswer(
	value: string | null | undefined,
): string[] {
	if (!value) return [];
	return value.split('').filter((c) => c.trim() !== '');
}

/** Junta as chaves na ordem das alternativas da pergunta. */
export function joinMultipleAnswer(
	question: Forms.FormQuestion,
	selectedKeys: string[],
): string {
	const order = (question.options ?? []).map((o) => o.key);
	return order.filter((k) => selectedKeys.includes(k)).join('');
}

export function isQuestionAnswered(
	question: Forms.FormQuestion,
	answers: Record<string, string | null>,
): boolean {
	const value = answers[String(question.number)];
	if (value == null) return false;
	if (question.type === 'MULTIPLE_CHOICE') {
		return splitMultipleAnswer(value).length > 0;
	}
	return String(value).trim().length > 0;
}

export function formatAnsweredAt(iso: string): string {
	try {
		return new Intl.DateTimeFormat('pt-BR', {
			day: '2-digit',
			month: 'long',
			year: 'numeric',
		}).format(new Date(iso));
	} catch {
		return iso;
	}
}
